/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth }   from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useToast }  from '../context/ToastContext';
import { groupsAPI, usersAPI } from '../utils/api';
import { getAvatarUrl, formatTime } from '../utils/helpers';
import { Users, Plus, Send, X, Search, Smile, Paperclip, Copy, Check } from 'lucide-react';
import './GroupsPage.css';

export default function GroupsPage() {
  useAuth(); // keeps auth context active for this page
  const { socket } = useSocket();
  const toast      = useToast();

  const [groups,    setGroups]    = useState([]);
  const [active,    setActive]    = useState(null);
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(true);
  const [showCreate,setShowCreate]= useState(false);
  const [newName,   setNewName]   = useState('');
  const [search,    setSearch]    = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [copied,    setCopied]    = useState(false);

  const messagesEnd = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    groupsAPI.getAll().catch(()=>({data:[]})).then(r => {
      setGroups(r.data||[]);
      if ((r.data||[]).length>0) setActive(r.data[0]);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    const gId = active._id||active.id;
    groupsAPI.getMessages(gId).catch(()=>({data:[]})).then(r => setMessages(r.data||[]));
    if (socket) socket.emit('group:join', { groupId:gId });
  }, [active, socket]);

  useEffect(() => {
    if (!socket) return;
    const handler = (msg) => {
      if (active && msg.group===(active._id||active.id)) setMessages(prev => [...prev, msg]);
    };
    socket.on('group:message', handler);
    return () => socket.off('group:message', handler);
  }, [socket, active]);

  useEffect(() => { messagesEnd.current?.scrollIntoView({behavior:'smooth'}); }, [messages]);

  // Search users for adding to new group
  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!search.trim()||search.trim().length<2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try { const {data} = await usersAPI.search(search.trim()); setSearchResults(data||[]); }
      catch { setSearchResults([]); }
    }, 350);
  }, [search]);

  const sendMessage = async () => {
    if (!input.trim()||!active) return;
    const content = input.trim(); setInput('');
    const gId = active._id||active.id;
    if (socket) socket.emit('group:message', { groupId:gId, content, type:'text' });
    else {
      try { const {data}=await groupsAPI.sendMessage(gId,{content,type:'text'}); setMessages(prev=>[...prev,data]); }
      catch { toast.error('Failed to send'); }
    }
  };

  const createGroup = async () => {
    if (!newName.trim()) { toast.error('Group name required'); return; }
    try {
      const memberIds = selectedMembers.map(m=>m._id||m.id);
      const { data } = await groupsAPI.create({ name:newName.trim(), memberIds });
      setGroups(prev => [data, ...prev]);
      setActive(data);
      setShowCreate(false); setNewName(''); setSelectedMembers([]); setSearch('');
      toast.success('Group created! 🎉');
    } catch { toast.error('Failed to create group'); }
  };

  const toggleMember = (u) => {
    setSelectedMembers(prev => {
      const id = u._id||u.id;
      const exists = prev.find(m=>(m._id||m.id)===id);
      return exists ? prev.filter(m=>(m._id||m.id)!==id) : [...prev, u];
    });
  };

  const copyInvite = () => {
    if (!active?.inviteLink) return;
    navigator.clipboard.writeText(`${window.location.origin}/groups/join/${active.inviteLink}`);
    setCopied(true);
    toast.success('Invite link copied!');
    setTimeout(()=>setCopied(false), 2000);
  };

  if (loading) return <div className="flex-center" style={{flex:1}}><div className="loader" style={{width:32,height:32,borderWidth:3}}/></div>;

  return (
    <div className="groups-page">

      {/* ── Groups list ── */}
      <div className="groups-list-col">
        <div className="comm-list-header">
          <h2>Groups</h2>
          <button className="btn-icon" onClick={()=>setShowCreate(true)}><Plus size={18}/></button>
        </div>
        <div className="comm-list-body">
          {groups.length===0 ? (
            <div style={{padding:'24px 14px',textAlign:'center',color:'var(--text-500)'}}>
              <Users size={32} style={{opacity:.3,margin:'0 auto 10px'}}/>
              <p style={{fontSize:'.82rem',fontWeight:500}}>No groups yet</p>
              <button className="btn btn-primary btn-sm" style={{marginTop:12}} onClick={()=>setShowCreate(true)}>
                <Plus size={13}/> Create Group
              </button>
            </div>
          ) : groups.map(g => (
            <div key={g._id||g.id} className={`comm-list-item ${active&&(active._id||active.id)===(g._id||g.id)?'active':''}`} onClick={()=>setActive(g)}>
              <div className="comm-avatar" style={{background:'linear-gradient(135deg,#00D68F,#00B4D8)'}}>
                {g.avatar ? <img src={g.avatar} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : g.name?.[0]?.toUpperCase()}
              </div>
              <div className="comm-list-info">
                <p className="comm-list-name">{g.name}</p>
                <p className="comm-list-members">{g.memberCount||g.members?.length||0} members</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Chat area ── */}
      {active ? (
        <div className="comm-chat-col">
          <div className="comm-chat-header">
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div className="comm-avatar" style={{width:36,height:36,fontSize:'.8rem',background:'linear-gradient(135deg,#00D68F,#00B4D8)'}}>{active.name?.[0]?.toUpperCase()}</div>
              <div>
                <p style={{fontWeight:700,fontSize:'.9rem'}}>{active.name}</p>
                <p style={{fontSize:'.72rem',color:'var(--text-500)'}}>{active.memberCount||active.members?.length||0} members</p>
              </div>
            </div>
            <button className="btn-icon" onClick={copyInvite} title="Copy invite link">
              {copied ? <Check size={17} style={{color:'var(--green)'}}/> : <Copy size={17}/>}
            </button>
          </div>

          <div className="comm-messages">
            {messages.length===0 ? (
              <div className="empty-state">
                <Users size={40}/>
                <h3>Welcome to {active.name}</h3>
                <p>This is the beginning of your group conversation</p>
              </div>
            ) : messages.map(msg => {
              const sender = msg.sender || msg.senderInfo || {};
              return (
                <div key={msg._id||msg.id} className="comm-msg-row">
                  <img src={getAvatarUrl(sender)} alt="" className="avatar" style={{width:36,height:36,flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:7}}>
                      <span style={{fontWeight:700,fontSize:'.85rem'}}>{sender.displayName||'User'}</span>
                      <span style={{fontSize:'.68rem',color:'var(--text-500)'}}>{formatTime(msg.createdAt)}</span>
                    </div>
                    <p style={{fontSize:'.88rem',marginTop:2,lineHeight:1.5,wordBreak:'break-word'}}>{msg.content}</p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEnd}/>
          </div>

          <div className="comm-input-bar">
            <button className="btn-icon input-btn"><Paperclip size={19}/></button>
            <div className="input-wrap">
              <input className="chat-input" placeholder={`Message ${active.name}`} value={input}
                onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();sendMessage();}}}/>
            </div>
            <button className="btn-icon input-btn"><Smile size={19}/></button>
            <button className={`send-btn ${input.trim()?'ready':''}`} onClick={sendMessage} disabled={!input.trim()}>
              <Send size={17}/>
            </button>
          </div>
        </div>
      ) : (
        <div className="empty-state" style={{flex:1}}>
          <Users size={48}/>
          <h3>No group selected</h3>
          <p>Create a group to start chatting with multiple people at once</p>
          <button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={14}/> Create Group</button>
        </div>
      )}

      {/* ── Create modal ── */}
      {showCreate && (
        <div className="modal-overlay" onClick={()=>setShowCreate(false)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Group</h2>
              <button className="btn-icon" onClick={()=>setShowCreate(false)}><X size={18}/></button>
            </div>
            <div className="form-group">
              <label>Group Name</label>
              <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. Weekend Trip 🏖️" autoFocus/>
            </div>
            <div className="form-group">
              <label>Add Members</label>
              <div className="conv-search" style={{margin:0}}>
                <Search size={14}/>
                <input placeholder="Search people..." value={search} onChange={e=>setSearch(e.target.value)}/>
              </div>
            </div>

            {selectedMembers.length > 0 && (
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12}}>
                {selectedMembers.map(m => (
                  <div key={m._id||m.id} style={{display:'flex',alignItems:'center',gap:5,background:'var(--primary-alpha)',borderRadius:'var(--r-full)',padding:'4px 10px 4px 4px'}}>
                    <img src={getAvatarUrl(m)} alt="" style={{width:20,height:20,borderRadius:'50%'}}/>
                    <span style={{fontSize:'.78rem',color:'var(--primary-light)'}}>{m.displayName}</span>
                    <button onClick={()=>toggleMember(m)} style={{background:'none',border:'none',color:'var(--primary-light)',cursor:'pointer',display:'flex'}}><X size={12}/></button>
                  </div>
                ))}
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{maxHeight:180,overflowY:'auto',marginBottom:16,border:'1px solid var(--border-1)',borderRadius:'var(--r-md)'}}>
                {searchResults.map(u => {
                  const selected = selectedMembers.some(m=>(m._id||m.id)===(u._id||u.id));
                  return (
                    <div key={u._id||u.id} className="contact-item" onClick={()=>toggleMember(u)} style={{background:selected?'var(--primary-alpha)':undefined}}>
                      <img src={getAvatarUrl(u)} alt="" className="avatar" style={{width:34,height:34}}/>
                      <div className="contact-info"><p className="contact-name">{u.displayName}</p></div>
                      {selected && <Check size={16} style={{color:'var(--primary-light)'}}/>}
                    </div>
                  );
                })}
              </div>
            )}

            <button className="btn btn-primary w-full" onClick={createGroup}>
              <Plus size={15}/> Create Group {selectedMembers.length>0 && `(${selectedMembers.length+1} members)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}